/**
 * Copyright 2024 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { GenAILiveClient } from "../lib/genai-live-client";
import { LiveClientOptions } from "../types";
import { AudioStreamer } from "../lib/audio-streamer";
import { audioContext } from "../lib/utils";
import VolMeterWorket from "../lib/worklets/vol-meter";
import { LiveConnectConfig } from "@google/genai";

export type UseLiveAPIResults = {
  client: GenAILiveClient;
  setConfig: (config: LiveConnectConfig) => void;
  config: LiveConnectConfig;
  model: string;
  setModel: (model: string) => void;
  connected: boolean;
  connect: () => Promise<void>;
  disconnect: () => Promise<void>;
  stopAudio: () => void;
  volume: number;
  audioStreamer: AudioStreamer | null;
};

export function useLiveAPI(options: LiveClientOptions): UseLiveAPIResults {
  const client = useMemo(() => new GenAILiveClient(options), [options]);
  const audioStreamerRef = useRef<AudioStreamer | null>(null);

  const [model, setModel] = useState<string>("models/gemini-2.0-flash-exp");
  const [config, setConfig] = useState<LiveConnectConfig>({});
  const [connected, setConnected] = useState(false);
  const [volume, setVolume] = useState(0);

  // register audio for streaming server -> speakers
  useEffect(() => {
    if (!audioStreamerRef.current) {
      audioContext({ id: "audio-out" }).then((audioCtx: AudioContext) => {
        audioStreamerRef.current = new AudioStreamer(audioCtx);
        audioStreamerRef.current
          .addWorklet<any>("vumeter-out", VolMeterWorket, (ev: any) => {
            setVolume(ev.data.volume);
          })
          .then(() => {
            // Successfully added worklet
          });
      });
    }
  }, [audioStreamerRef]);

  useEffect(() => {
    const onOpen = () => {
      console.log("🔓 [GEMINI] Connection opened - setting connected state to true");
      setConnected(true);
    };

    const onClose = () => {
      console.log("🔒 [GEMINI] Connection closed - setting connected state to false");
      setConnected(false);
    };

    const onError = (error: ErrorEvent) => {
      console.error("❌ [GEMINI] Connection error:", error);
    };

    const stopAudioStreamer = () => {
      console.log("🔇 [GEMINI] Audio streaming interrupted");
      audioStreamerRef.current?.stop();
    };

    const onAudio = (data: ArrayBuffer) => {
      console.log(`🔊 [GEMINI] Received audio data from Gemini: ${data.byteLength} bytes`);
      audioStreamerRef.current?.addPCM16(new Uint8Array(data));
    };

    client
      .on("error", onError)
      .on("open", onOpen)
      .on("close", onClose)
      .on("interrupted", stopAudioStreamer)
      .on("audio", onAudio);

    return () => {
      // Stop audio streaming immediately on cleanup
      audioStreamerRef.current?.stop();
      client
        .off("error", onError)
        .off("open", onOpen)
        .off("close", onClose)
        .off("interrupted", stopAudioStreamer)
        .off("audio", onAudio)
        .disconnect();
    };
  }, [client]);

  const connect = useCallback(async () => {
    if (!config) {
      throw new Error("config has not been set");
    }
    console.log("🔌 [GEMINI] Attempting to connect to Gemini Live API via use-live-api hook");
    console.log("📋 [GEMINI] Model:", model);
    console.log("⚙️ [GEMINI] Config:", JSON.stringify(config, null, 2));
    
    client.disconnect();
    await client.connect(model, config);
    console.log("✅ [GEMINI] Connection established via use-live-api hook");
  }, [client, config, model]);

  const disconnect = useCallback(async () => {
    console.log("🔌 [GEMINI] Disconnecting from Gemini Live API via use-live-api hook");
    // Stop audio streaming immediately
    audioStreamerRef.current?.stop();
    // Disconnect the client
    client.disconnect();
    setConnected(false);
    console.log("✅ [GEMINI] Disconnected from Gemini Live API via use-live-api hook");
  }, [setConnected, client]);

  const stopAudio = useCallback(() => {
    console.log("🔇 [GEMINI] Stopping audio streaming");
    audioStreamerRef.current?.stop();
  }, []);

  return {
    client,
    config,
    setConfig,
    model,
    setModel,
    connected,
    connect,
    disconnect,
    stopAudio,
    volume,
    audioStreamer: audioStreamerRef.current, // Expose audioStreamer for mobile audio fix
  };
}
